
import { useState } from "react"
import {
  Sparkles,
  Send,
  Loader2,
  Copy,
  Check,
  BarChart3,
  Users,
  DollarSign,
  Target,
} from "lucide-react"
import { cn } from "@/lib/utils"
import type { InvestorType } from "@/lib/types"
import { useIntercom } from "@/hooks/use-intercom"
import { createUpdate, createActivity } from "@/lib/api-client"
import { useUpdates } from "@/lib/use-api"

const API_BASE = "https://irene-api.heysalad.app"

function getAuthToken(): string | null {
  if (typeof document === "undefined") return null
  const match = document.cookie.match(/(?:^|; )irene_token=([^;]*)/)
  return match ? decodeURIComponent(match[1]) : null
}

const toneOptions: { value: InvestorType; label: string; description: string }[] = [
  {
    value: "data-driven",
    label: "Data-Driven",
    description: "Heavy on metrics, unit economics, and growth numbers",
  },
  {
    value: "vision-focused",
    label: "Vision-Focused",
    description: "Emphasizes market opportunity, product vision, and team",
  },
  {
    value: "milestone-based",
    label: "Milestone-Based",
    description: "Structured around achievements and upcoming targets",
  },
]

export default function UpdateGeneratorPage() {
  const [mrr, setMrr] = useState("")
  const [users, setUsers] = useState("")
  const [runway, setRunway] = useState("")
  const [milestones, setMilestones] = useState("")
  const [tone, setTone] = useState<InvestorType>("data-driven")
  const [additionalContext, setAdditionalContext] = useState("")
  const [generatedUpdate, setGeneratedUpdate] = useState("")
  const { trackUpdateGenerated } = useIntercom()
  const [isGenerating, setIsGenerating] = useState(false)
  const [copied, setCopied] = useState(false)

  async function handleGenerate() {
    setIsGenerating(true)
    setGeneratedUpdate("")

    try {
      const token = getAuthToken()
      const headers: Record<string, string> = { "Content-Type": "application/json" }
      if (token) headers["Authorization"] = `Bearer ${token}`

      const response = await fetch(`${API_BASE}/api/generate-update`, {
        method: "POST",
        headers,
        mode: "cors",
        body: JSON.stringify({
          mrr,
          users,
          runway,
          milestones: milestones.split("\n").filter(Boolean),
          tone,
          additionalContext,
        }),
      })

      const json = await response.json() as { success?: boolean; data?: any; text?: string; error?: string; message?: string }

      if (!response.ok) {
        throw new Error(json.error || json.message || `Generation failed (${response.status})`)
      }

      // Backend wraps in { success, data: { text } } via jsonResponse
      const payload = json.data || json
      const updateText = payload.text || ""

      if (!updateText) {
        throw new Error("No update text was generated. Please try again.")
      }

      setGeneratedUpdate(updateText)
      trackUpdateGenerated({ tone, hasMetrics: Boolean(mrr || users || runway) })

      // Save generated update to API
      try {
        await createUpdate({
          title: `${tone} Update - ${new Date().toLocaleDateString()}`,
          content: updateText,
          subject: `${tone} Update - ${new Date().toLocaleDateString()}`,
          generated_content: updateText,
          tone,
          mrr,
          users_count: users,
          runway,
          milestones: milestones.split("\n").filter(Boolean).join(", "),
          additional_context: additionalContext,
          status: "draft",
        } as any)
        await createActivity({
          type: "update",
          title: "Generated investor update",
          description: `Created a ${tone} update with MRR: ${mrr || "N/A"}`,
        })
      } catch {
        // Non-critical: update was generated even if save fails
      }
    } catch (err) {
      setGeneratedUpdate(
        `Failed to generate update: ${err instanceof Error ? err.message : "Unknown error"}. Please check your connection and try again.`
      )
    } finally {
      setIsGenerating(false)
    }
  }

  function handleCopy() {
    navigator.clipboard.writeText(generatedUpdate)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <div className="p-6 lg:p-8">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-foreground">
          Smart Update Generator
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Input your metrics and let Irene craft personalized investor updates.
        </p>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Input Form */}
        <div className="flex flex-col gap-5">
          {/* Metrics */}
          <div className="glass-strong rounded-xl p-5 shadow-sm">
            <h3 className="text-sm font-semibold text-foreground">
              Monthly Metrics
            </h3>
            <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground mb-1.5">
                  <DollarSign className="h-3 w-3" />
                  Monthly Recurring Revenue
                </label>
                <input
                  type="text"
                  value={mrr}
                  onChange={(e) => setMrr(e.target.value)}
                  placeholder="e.g. $45,000"
                  className="w-full rounded-lg border border-input bg-card px-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                />
              </div>
              <div>
                <label className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground mb-1.5">
                  <Users className="h-3 w-3" />
                  Active Users
                </label>
                <input
                  type="text"
                  value={users}
                  onChange={(e) => setUsers(e.target.value)}
                  placeholder="e.g. 2,400"
                  className="w-full rounded-lg border border-input bg-card px-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                />
              </div>
              <div>
                <label className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground mb-1.5">
                  <BarChart3 className="h-3 w-3" />
                  Runway (months)
                </label>
                <input
                  type="text"
                  value={runway}
                  onChange={(e) => setRunway(e.target.value)}
                  placeholder="e.g. 14 months"
                  className="w-full rounded-lg border border-input bg-card px-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                />
              </div>
              <div>
                <label className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground mb-1.5">
                  <Target className="h-3 w-3" />
                  Key Milestones (one per line)
                </label>
                <textarea
                  value={milestones}
                  onChange={(e) => setMilestones(e.target.value)}
                  placeholder={"Launched v2.0\nHired CTO\nReached 2k users"}
                  rows={3}
                  className="w-full rounded-lg border border-input bg-card px-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring resize-none"
                />
              </div>
            </div>
          </div>

          {/* Tone Selection */}
          <div className="glass-strong rounded-xl p-5 shadow-sm">
            <h3 className="text-sm font-semibold text-foreground">
              Update Tone
            </h3>
            <div className="mt-3 flex flex-col gap-2">
              {toneOptions.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => setTone(option.value)}
                  className={cn(
                    "flex flex-col items-start rounded-lg border p-3 text-left transition-all",
                    tone === option.value
                      ? "border-primary bg-primary/5"
                      : "border-input hover:border-primary/30 hover:bg-accent/50"
                  )}
                >
                  <span className="text-sm font-medium text-foreground">
                    {option.label}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {option.description}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Additional Context */}
          <div className="glass-strong rounded-xl p-5 shadow-sm">
            <h3 className="text-sm font-semibold text-foreground">
              Additional Context
            </h3>
            <textarea
              value={additionalContext}
              onChange={(e) => setAdditionalContext(e.target.value)}
              placeholder="Any specific asks, challenges, or highlights you want to include..."
              rows={3}
              className="mt-3 w-full rounded-lg border border-input bg-card px-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring resize-none"
            />
          </div>

          {/* Generate Button */}
          <button
            type="button"
            onClick={handleGenerate}
            disabled={isGenerating || !mrr}
            className="flex items-center justify-center gap-2 rounded-lg bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-50"
          >
            {isGenerating ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Generating with AI...
              </>
            ) : (
              <>
                <Sparkles className="h-4 w-4" />
                Generate Update
              </>
            )}
          </button>
        </div>

        {/* Output */}
        <div className="flex flex-col gap-5">
          <div className="glass-strong rounded-xl p-5 shadow-sm min-h-[600px] flex flex-col">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-primary" />
                <h3 className="text-sm font-semibold text-foreground">
                  Generated Update
                </h3>
              </div>
              {generatedUpdate && (
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={handleCopy}
                    className="flex items-center gap-1.5 rounded-lg border border-input px-3 py-1.5 text-xs font-medium text-foreground hover:bg-accent transition-colors"
                  >
                    {copied ? (
                      <Check className="h-3 w-3 text-emerald-500" />
                    ) : (
                      <Copy className="h-3 w-3" />
                    )}
                    {copied ? "Copied" : "Copy"}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      if (generatedUpdate) {
                        navigator.clipboard.writeText(generatedUpdate)
                      }
                    }}
                    className="flex items-center gap-1.5 rounded-lg bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:bg-primary/90 transition-colors"
                    title="Copy and send via your email client"
                  >
                    <Send className="h-3 w-3" />
                    Copy & Send
                  </button>
                </div>
              )}
            </div>

            {generatedUpdate ? (
              <div className="mt-4 flex-1">
                <div className="rounded-lg bg-muted/50 p-4">
                  <p className="text-sm text-foreground leading-relaxed whitespace-pre-wrap">
                    {generatedUpdate}
                  </p>
                </div>
              </div>
            ) : (
              <div className="mt-4 flex flex-1 items-center justify-center">
                <div className="text-center">
                  <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-muted">
                    <Sparkles className="h-6 w-6 text-muted-foreground" />
                  </div>
                  <p className="mt-3 text-sm font-medium text-muted-foreground">
                    Your AI-generated update will appear here
                  </p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Fill in your metrics and click Generate
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
